import { camelCase } from 'lodash'
import * as actions from './actions'

const sanitizeObject = obj => Object.entries(obj).reduce((acc, [key, value]) => {
  acc[camelCase(key)] = value
  return acc
}, {})

const defaultState = {
  files: {
    isFetching: true,
    error: null,
    data: null,
    lastFetched: null,
    fileIntervalId: null,
    filterQuery: '',
    sorting: {
      order: 'ascending',
      column: 'fileName',
    },
  },
  staging: {
    files: [],
    isSearchable: {},
    groupRestrictions: {},
    dataFileNeedsCleaning: {},
    isUploading: {},
    isComplete: {},
    errors: {},
  },
  updateFile: {
    isFetching: false,
    error: null,
    data: null,
    lastFetched: null,
  },
  deleteFile: {
    isFetching: false,
    error: null,
    data: null,
    lastFetched: null,
  },
  currentDocument: {
    isFetching: false,
    error: null,
    data: null,
    lastFetched: null,
  },
  fileDetails: {
    isFetching: false,
    error: null,
    data: null,
    lastFetched: null,
  },
}

export default function filesReducer(state = defaultState, action = {}) {
  switch (action.type) {
    case actions.FETCH_FILES_LOADING: {
      return {
        ...state,
        files: {
          ...state.files,
          isFetching: true,
          error: null,
        },
      }
    }
    case actions.FETCH_FILES_SUCCESS: {
      const lastFetched = Date.now()
      return {
        ...state,
        files: {
          ...state.files,
          isFetching: false,
          lastFetched,
          data: action.files.map(sanitizeObject),
          error: null,
        },
      }
    }
    case actions.FETCH_FILES_ERROR: {
      const lastFetched = Date.now()
      return {
        ...state,
        files: {
          ...state.files,
          isFetching: false,
          lastFetched,
          error: action.error,
        },
      }
    }
    case actions.SET_FILES_FILTER_QUERY: {
      return {
        ...state,
        files: {
          ...state.files,
          filterQuery: action.filterQuery ?? '',
        },
      }
    }
    case actions.STAGE_FILE_FOR_UPLOAD: {
      return {
        ...state,
        staging: {
          ...state.staging,
          files: [
            ...state.staging.files,
            action.file,
          ],
          groupRestrictions: {
            ...state.staging.groupRestrictions,
            [action.file.name]: action.groupRestrictions,
          },
          isSearchable: {
            ...state.staging.isSearchable,
            [action.file.name]: action.isSearchable,
          },
          dataFileNeedsCleaning: {
            ...state.staging.dataFileNeedsCleaning,
            [action.file.name]: action.needsCleaning,
          },
        },
      }
    }
    case actions.UPDATE_SEARCHABLE_FOR_STAGED_FILE: {
      return {
        ...state,
        staging: {
          ...state.staging,
          isSearchable: {
            ...state.staging.isSearchable,
            [action.fileName]: action.isSearchable,
          },
          errors: {
            ...state.staging.errors,
            [action.fileName]: null,
          },
        },
      }
    }
    case actions.UPDATE_ACLS_FOR_STAGED_FILE: {
      return {
        ...state,
        staging: {
          ...state.staging,
          groupRestrictions: {
            ...state.staging.groupRestrictions,
            [action.fileName]: {
              ...state.staging.groupRestrictions[action.fileName],
              [action.groupName]: action.isActive ?? !state.staging.groupRestrictions[action.fileName][action.groupName],
            },
          },
          errors: {
            ...state.staging.errors,
            [action.fileName]: null,
          },
        },
      }
    }
    case actions.UPDATE_DATA_FILE_CLEANING: {
      return {
        ...state,
        staging: {
          ...state.staging,
          dataFileNeedsCleaning: {
            ...state.staging.dataFileNeedsCleaning,
            [action.fileName]: action.needsCleaning ?? false,
          },
          errors: {
            ...state.staging.errors,
            [action.fileName]: null,
          },
        },
      }
    }
    case actions.UNSTAGE_FILE_FOR_UPLOAD: {
      return {
        ...state,
        staging: {
          ...state.staging,
          files: state.staging.files.filter(file => file.name !== action.fileName),
          groupRestrictions: {
            ...state.staging.groupRestrictions,
            [action.fileName]: [],
          },
          isSearchable: {
            ...state.staging.isSearchable,
            [action.fileName]: false,
          },
          dataFileNeedsCleaning: {
            ...state.staging.dataFileNeedsCleaning,
            [action.fileName]: false,
          },
          isUploading: {
            ...state.staging.isUploading,
            [action.fileName]: false,
          },
          isComplete: {
            ...state.staging.isComplete,
            [action.fileName]: false,
          },
          errors: {
            ...state.staging.errors,
            [action.fileName]: false,
          },
        },
      }
    }
    case actions.UNSTAGE_ALL_FILES_FOR_UPLOAD: {
      return {
        ...state,
        staging: {
          ...state.staging,
          groupRestrictions: {},
          dataFileNeedsCleaning: {},
          isSearchable: {},
          isUploading: {},
          isComplete: {},
          errors: {},
          files: [],
        },
      }
    }
    case actions.CREATE_FILE_LOADING: {
      return {
        ...state,
        staging: {
          ...state.staging,
          isUploading: {
            ...state.staging.isUploading,
            [action.fileName]: true,
          },
          isComplete: {
            ...state.staging.isComplete,
            [action.fileName]: false,
          },
          errors: {
            ...state.staging.errors,
            [action.fileName]: null,
          },
        },
      }
    }
    case actions.CREATE_FILE_SUCCESS: {
      const lastFetched = Date.now()
      return {
        ...state,
        files: {
          ...state.files,
          lastFetched,
          data: [...state.files.data, sanitizeObject(action.payload)],
        },
        staging: {
          ...state.staging,
          isUploading: {
            ...state.staging.isUploading,
            [action.fileName]: false,
          },
          isComplete: {
            ...state.staging.isComplete,
            [action.fileName]: true,
          },
          errors: {
            ...state.staging.errors,
            [action.fileName]: null,
          },
        },
      }
    }
    case actions.CREATE_FILE_ERROR: {
      return {
        ...state,
        staging: {
          ...state.staging,
          isUploading: {
            ...state.staging.isUploading,
            [action.fileName]: false,
          },
          errors: {
            ...state.staging.errors,
            [action.fileName]: action.error,
          },
        },
      }
    }
    case actions.REPLACE_FILE_LOADING: {
      return {
        ...state,
        staging: {
          ...state.staging,
          isUploading: {
            ...state.staging.isUploading,
            [action.fileName]: true,
          },
          isComplete: {
            ...state.staging.isComplete,
            [action.fileName]: false,
          },
          errors: {
            ...state.staging.errors,
            [action.fileName]: null,
          },
        },
      }
    }
    case actions.REPLACE_FILE_SUCCESS: {
      const lastFetched = Date.now()
      const payload = sanitizeObject(action.payload)

      const newFiles = (state.files.data || []).map(file => {
        if (file.name === payload.name && file.fileType === payload.fileType) {
          return payload
        }
        return file
      })

      return {
        ...state,
        files: {
          ...state.files,
          lastFetched,
          data: newFiles,
        },
        staging: {
          ...state.staging,
          isUploading: {
            ...state.staging.isUploading,
            [action.fileName]: false,
          },
          isComplete: {
            ...state.staging.isComplete,
            [action.fileName]: true,
          },
          errors: {
            ...state.staging.errors,
            [action.fileName]: null,
          },
        },
      }
    }
    case actions.REPLACE_FILE_ERROR: {
      return {
        ...state,
        staging: {
          ...state.staging,
          isUploading: {
            ...state.staging.isUploading,
            [action.fileName]: false,
          },
          errors: {
            ...state.staging.errors,
            [action.fileName]: action.error,
          },
        },
      }
    }
    case actions.UPDATE_FILE_LOADING: {
      return {
        ...state,
        updateFile: {
          ...state.updateFile,
          isFetching: true,
          error: null,
          data: action.payload,
        },
      }
    }
    case actions.UPDATE_FILE_SUCCESS: {
      const lastFetched = Date.now()
      const { payload } = action

      const newFiles = (state.files.data || []).map(file => {
        if (file.id === payload.id) {
          return sanitizeObject(payload)
        }
        return file
      })

      return {
        ...state,
        files: {
          ...state.files,
          lastFetched,
          data: newFiles || state.files.data,
        },
        updateFile: {
          ...state.updateFile,
          isFetching: false,
          lastFetched,
          data: action.payload,
          error: null,
        },
      }
    }
    case actions.UPDATE_FILE_ERROR: {
      const lastFetched = Date.now()
      return {
        ...state,
        updateFile: {
          ...state.updateFile,
          isFetching: false,
          lastFetched,
          error: action.payload,
        },
      }
    }
    case actions.DELETE_FILE_LOADING: {
      return {
        ...state,
        deleteFile: {
          ...state.deleteFile,
          isFetching: true,
          error: null,
          data: null,
        },
      }
    }
    case actions.DELETE_FILE_SUCCESS: {
      const lastFetched = Date.now()

      const files = state.files.data ?? []
      const newFiles = files.filter(file => !action.fileIDs.includes(file.id))

      return {
        ...state,
        files: {
          ...state.files,
          lastFetched,
          data: newFiles,
        },
        deleteFile: {
          ...state.deleteFile,
          isFetching: false,
          lastFetched,
          error: null,
        },
      }
    }
    case actions.DELETE_FILE_ERROR: {
      const lastFetched = Date.now()
      return {
        ...state,
        deleteFile: {
          ...state.deleteFile,
          isFetching: false,
          lastFetched,
          error: action.payload,
        },
      }
    }
    case actions.FETCH_DOCUMENT_LOADING: {
      return {
        ...state,
        currentDocument: {
          ...state.currentDocument,
          isFetching: true,
          error: null,
        },
      }
    }
    case actions.FETCH_DOCUMENT_SUCCESS: {
      const lastFetched = Date.now()
      return {
        ...state,
        currentDocument: {
          ...state.currentDocument,
          isFetching: false,
          lastFetched,
          data: action.payload,
          error: null,
        },
      }
    }
    case actions.FETCH_DOCUMENT_ERROR: {
      const lastFetched = Date.now()
      return {
        ...state,
        currentDocument: {
          ...state.currentDocument,
          isFetching: false,
          lastFetched,
          error: action.payload,
        },
      }
    }

    case actions.FILE_DETAILS_FETCH_LOADING: {
      return {
        ...state,
        fileDetails: {
          ...state.fileDetails,
          isFetching: true,
          error: null,
        },
      }
    }
    case actions.FILE_DETAILS_FETCH_SUCCESS: {
      const lastFetched = Date.now()
      const payload = sanitizeObject(action.payload)

      return {
        ...state,
        fileDetails: {
          ...state.fileDetails,
          isFetching: false,
          lastFetched,
          data: payload,
          error: null,
        },
      }
    }
    case actions.FILE_DETAILS_FETCH_ERROR: {
      const lastFetched = Date.now()
      return {
        ...state,
        fileDetails: {
          ...state.fileDetails,
          isFetching: false,
          lastFetched,
          error: action.payload,
        },
      }
    }
    case actions.SET_SORT_COLUMN: {
      return {
        ...state,
        files: {
          ...state.files,
          sorting: {
            ...state.files.sorting,
            column: action.column,
          },
        },
      }
    }
    case actions.SET_SORT_ORDER: {
      return {
        ...state,
        files: {
          ...state.files,
          sorting: {
            ...state.files.sorting,
            order: action.order,
          },
        },
      }
    }
    case actions.SET_FILE_INTERVAL_ID: {
      return {
        ...state,
        files: {
          ...state.files,
          fileIntervalId: action.fileIntervalId,
        },
      }
    }
    case actions.CLEAR_FILE_INTERVAL_ID: {
      return {
        ...state,
        files: {
          ...state.files,
          fileIntervalId: null,
        },
      }
    }

    default:
      return state
  }
}
